import {FC, useEffect, useMemo, useState} from "react"
import axios from "axios"
import moment from "moment"
import "moment/locale/de"
import "chartjs-adapter-moment"

import {Badge, Card, Col, Container, Row} from "react-bootstrap"
import {Line} from "react-chartjs-2"

import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Tooltip,
    Legend,
    Title,
    ChartData,
    ChartOptions,
    Plugin,
} from "chart.js"
import {useTranslation} from "react-i18next";
import {useTheme} from "../../context/ThemeProvider";
import BuildingOverviewComponent from "../BuildingView/BuildingComponent";

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Tooltip,
    Legend,
    Title
)

type SiteInfoComponentProps = {}

type ClimateEntry = {
    _id: string
    id: string
    temperature: number
    createdAt?: string
    timestamp?: string
    time?: string
    date?: string
}

type ChartItem = ClimateEntry & {
    chartDate: moment.Moment
}

const createDayMarkerPlugin = (
    chartItems: ChartItem[],
    dayLineColor: string,
    dayTextColor: string
): Plugin<"line"> => ({
    id: "dayMarkerPlugin",

    afterDraw(chart) {
        const {ctx, chartArea, scales} = chart
        const drawnDays = new Set<string>()

        chartItems.forEach(item => {
            const day = item.chartDate.format("YYYY-MM-DD")

            if (drawnDays.has(day)) return
            drawnDays.add(day)

            const x = scales.x.getPixelForValue(item.chartDate.valueOf())

            if (x < chartArea.left || x > chartArea.right) return

            ctx.save()

            ctx.beginPath()
            ctx.moveTo(x, chartArea.top)
            ctx.lineTo(x, chartArea.bottom)
            ctx.setLineDash([5, 5])
            ctx.lineWidth = 1
            ctx.strokeStyle = dayLineColor
            ctx.stroke()

            ctx.fillStyle = dayTextColor
            ctx.font = "12px sans-serif"
            ctx.fillText(item.chartDate.format("DD.MM."), x + 6, chartArea.top + 16)

            ctx.restore()
        })
    },
})

const SiteInfoComponent: FC<SiteInfoComponentProps> = () => {
    const {t} = useTranslation("site")
    const theme = useTheme()

    const [data, setData] = useState<ClimateEntry[]>([])
    const [site, setSite] = useState<any>()

    useEffect(() => {

        axios
            .get<ClimateEntry[]>("/api/climate/history/6784d8aeb2e2dd156850e00a")
            .then(result => {
                setData(result.data)
                axios.get("/api/site/get/6784d8aeb2e2dd156850e00a").then(result => {
                    setSite(result.data)
                })
            })
    }, [])

    const isDark = theme.getTheme() === "dark"

    const chartColors = {
        line: isDark ? "#ffffff" : "#0d6efd",
        point: isDark ? "#ffffff" : "#0d6efd",
        pointHoverBorder: isDark ? "#0d6efd" : "#212529",
        grid: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        tick: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)",
        title: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.75)",
        dayLine: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.22)",
        dayText: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.75)",
    }

    const chartItems = useMemo<ChartItem[]>(() => {
        return data
            .map(item => {
                const rawDate = item.createdAt ?? item.timestamp ?? item.time ?? item.date
                const chartDate = rawDate ? moment(rawDate) : null

                if (!chartDate || !chartDate.isValid()) {
                    return null
                }

                return {
                    ...item,
                    chartDate,
                }
            })
            .filter((item): item is ChartItem => item !== null)
            .reverse()
    }, [data])

    const chartData: ChartData<"line", { x: Date; y: number }[]> = {
        datasets: [
            {
                label: t("site.temperatureWithUnit"),
                data: chartItems.map(item => ({
                    x: item.chartDate.toDate(),
                    y: Number(item.temperature),
                })),

                // Linie
                borderColor: chartColors.line,
                borderWidth: 2,
                tension: 0.4,
                fill: false,

                // Messpunkte
                pointRadius: 3,
                pointHoverRadius: 6,
                pointHitRadius: 10,

                pointBackgroundColor: chartColors.point,
                pointBorderWidth: 2,

                // Hover
                pointHoverBackgroundColor: chartColors.point,
                pointHoverBorderColor: chartColors.pointHoverBorder,
                pointHoverBorderWidth: 3,
            },
        ],
    }

    const chartOptions: ChartOptions<"line"> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: "time",
                time: {
                    unit: "hour",
                    tooltipFormat: "DD.MM.YYYY HH:mm",
                    displayFormats: {
                        hour: "HH:mm",
                        day: "DD.MM.",
                    },
                },
                grid: {
                    color: chartColors.grid,
                },
                ticks: {
                    color: chartColors.tick,
                    autoSkip: true,
                    maxTicksLimit: 20,
                    callback: value => moment(value).format("HH:mm"),
                },
                title: {
                    display: true,
                    text: t("site.time"),
                    color: chartColors.title,
                },
            },
            y: {
                grid: {
                    color: chartColors.grid,
                },
                ticks: {
                    color: chartColors.tick,
                },
                title: {
                    display: true,
                    text: t("site.temperatureWithUnit"),
                    color: chartColors.title,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: items =>
                        moment(items[0].parsed.x).format("DD.MM.YYYY HH:mm"),
                    label: item => {
                        const y = item.parsed.y
                        return y == null
                            ? ""
                            : t("site.temperatureTooltip", {
                                value: y.toFixed(1),
                            })
                    },
                },
            },
        },
    }

    return (
        <div className="component">
            <Container fluid className="py-3">
                {site && (<BuildingOverviewComponent building={site.building}/>)}
                <Row className="g-3 mt-3">
                    <Col xs={12}>
                        <Card>
                            <Card.Header>
                                <div className="d-flex align-items-center justify-content-start gap-3">
                                    <h4 className="mb-0 text-truncate">
                                        <i className="bi bi-thermometer-half"></i>{" "}
                                        {t("site.temperatureHistory")}
                                    </h4>
                                    {site?.room && (
                                        <Badge
                                            bg="danger"
                                            pill
                                            className="fs-6 px-3 py-2 flex-shrink-0"
                                        >
                                            {t("site.room")} {site.room}
                                        </Badge>
                                    )}
                                </div>
                            </Card.Header>

                            <Card.Body>
                                {chartItems.length === 0 ? (
                                    <p>{t("site.noTemperatureData")}</p>
                                ) : (
                                    <div style={{height: "420px"}}>
                                        <Line
                                            data={chartData}
                                            options={chartOptions}
                                            plugins={[
                                                createDayMarkerPlugin(
                                                    chartItems,
                                                    chartColors.dayLine,
                                                    chartColors.dayText
                                                )
                                            ]}
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default SiteInfoComponent